<template>
  <div class="main-component-wrapper is-fullheight">
    <rbz-header-main class="header-main" ref="rbzHeaderMainRef" />
    <rbz-side-menu class="side-menu" />
    <div
      class="content-wrapper"
      :style="`--header-main-height: ${headerMainHeight}px; --banner-height: ${bannerHeight}px; --side-menu-width: ${sideMenuWidth}px`"
    >
      <div class="rbz-banner" v-if="isUpdateInProcess" ref="rbzBannerRef">
        <icon-warning-black />
        <span class="rbz-text-large">
          {{ $t('update.updateInProcessBanner') }}
        </span>
      </div>
      <router-view class="is-fullheight main-content" ref="viewCardScroll"> </router-view>
    </div>
    <idle-modal />
    <update-modal />
    <tags-panel :headerMainHeight="headerMainHeight"></tags-panel>
    <editor-display-modal />
  </div>
</template>

<script lang="ts">
import RbzHeaderMain from '@/components/RbzHeaderMain.vue';
import RbzSideMenu from '@/components/RbzSideMenu.vue';
import { defineComponent } from 'vue';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import IdleModal from '@/components/IdleModal.vue';
import { useUserStore } from '@/store/UserStore';
import { useTagsStore } from '@/store/TagsStore';
import { useDatasetsStore } from '@/store/DatasetsStore';
import TagsPanel from '@/components/TagsPanel.vue';
import { usePlanetStore } from '@/store/PlanetStore';
import { useSideMenuStore } from '@/store/SideMenuStore';
import IconWarningBlack from 'ui-component-library/assets/icons/IconWarningBlack.vue';
import UpdateModal from '@/components/UpdateModal.vue';
import EditorDisplayModal from '@/components/EditorDisplayModal.vue';
import { ENV, SIDE_MENU } from '@/constants';

export default defineComponent({
  name: 'MainComponent',
  components: {
    EditorDisplayModal,
    IconWarningBlack,
    IdleModal,
    RbzHeaderMain,
    RbzSideMenu,
    TagsPanel,
    UpdateModal,
  },
  data() {
    return {
      headerMainHeight: 56,
      bannerHeight: 0,
    };
  },
  watch: {
    $route: {
      handler: async function (val, oldVal) {
        document.title = `${ENV.VITE_RBZ_PLANET || 'WAAP Console'} | ${val.name}`;
        if (val && val !== oldVal && this.$refs.viewCardScroll) {
          this.$refs.viewCardScroll.scrollTop = 0;
        }
      },
      immediate: true,
      deep: true,
    },
  },
  computed: {
    ...mapStores(
      useBranchesStore,
      useUserStore,
      usePlanetStore,
      useTagsStore,
      useDatasetsStore,
      useSideMenuStore
    ),

    isUpdateInProcess() {
      return this.planetStore.isUpdateInProcess;
    },

    sideMenuWidth() {
      const isCollapsed = this.sideMenuStore?.isCollapsed ?? true;
      return isCollapsed ? SIDE_MENU.WIDTH_COLLAPSED : SIDE_MENU.WIDTH_EXPANDED;
    },
  },
  async beforeMount() {
    await this.branchesStore.loadBranches();
    const loadTagsPromise = this.tagsStore.loadTags();
    const loadContentFilterRulesTagsPromise = this.tagsStore.loadContentFilterRulesTags();
    const idleTrackerPromise = this.userStore.idleTrackerStart();
    const planetStatusProcessTrackerPromise = this.planetStore.planetStatusProcessTrackerStart();
    this.planetStore.setVersionFromEnvVar();
    const loadAsnsPromise = this.datasetsStore.loadAsns();
    const loadCountriesPromise = this.datasetsStore.loadCountries();
    await Promise.all([
      loadTagsPromise,
      loadContentFilterRulesTagsPromise,
      idleTrackerPromise,
      planetStatusProcessTrackerPromise,
      loadAsnsPromise,
      loadCountriesPromise,
    ]);
  },
  updated() {
    this.headerMainHeight = this.$refs.rbzHeaderMainRef?.$el?.getBoundingClientRect()?.height || 56;
    this.bannerHeight = this.$refs.rbzBannerRef?.getBoundingClientRect()?.height || 0;
  },
});
</script>
<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

html {
  background: hsl(206deg, 23%, 94%);
}

.side-menu {
  z-index: 90101;
}

.content-wrapper {
  height: calc(100% - var(--header-main-height) - var(--banner-height));
  margin-left: var(--side-menu-width, 90px);
  width: calc(100% - var(--side-menu-width, 90px));
}

.rbz-banner {
  background-color: map.get(colors.$colors, 'marigold-yellow');
  box-sizing: content-box;
  display: flex;
  gap: 8px;
  height: 20px;
  justify-content: center;
  padding: 15px 0;
  position: relative;
}

.main-content {
  height: 100%;
  max-width: 100%;
  min-width: 100%;
  position: relative;
  width: 100%;
}
</style>
